(() => {
  const THEME_STORAGE_KEY = 'beyimen-theme';
  const MEDIA_ROLE_ORDER = ['main', 'lifestyle', 'dimensions', 'installation', 'shared'];

  const optionValueAvailable = (variants, position, value) => variants.some((variant) => (
    variant.available && variant.options[position] === value
  ));

  const chooseCompatibleVariant = (variants, selectedOptions, position, value) => {
    let bestVariant;
    let bestScore = -1;

    variants.forEach((variant) => {
      if (!variant.available || variant.options[position] !== value) return;
      const score = variant.options.reduce((matches, optionValue, optionPosition) => (
        optionPosition !== position && optionValue === selectedOptions[optionPosition] ? matches + 1 : matches
      ), 0);
      if (score > bestScore) {
        bestVariant = variant;
        bestScore = score;
      }
    });

    return bestVariant;
  };

  const orderVariantMedia = (mediaItems, featuredMediaId) => [...mediaItems].sort((left, right) => {
    const leftFeatured = String(left.id) === String(featuredMediaId);
    const rightFeatured = String(right.id) === String(featuredMediaId);
    if (leftFeatured !== rightFeatured) return leftFeatured ? -1 : 1;
    const leftRole = MEDIA_ROLE_ORDER.indexOf(left.role);
    const rightRole = MEDIA_ROLE_ORDER.indexOf(right.role);
    return (leftRole === -1 ? MEDIA_ROLE_ORDER.length : leftRole) - (rightRole === -1 ? MEDIA_ROLE_ORDER.length : rightRole);
  });

  const selectVariantMedia = (mediaItems, variants, variant) => {
    const variantId = String(variant.id);
    const featuredMediaIds = new Set(variants.map((item) => item.featuredMediaId).filter(Boolean).map(String));
    const preferredIds = [variant.featuredMediaId, ...(variant.galleryMediaIds || [])].filter(Boolean).map(String);
    const ordered = [];
    const addMedia = (media) => {
      if (media && !ordered.some((item) => String(item.id) === String(media.id))) ordered.push(media);
    };

    preferredIds.forEach((id) => addMedia(mediaItems.find((media) => String(media.id) === id)));
    mediaItems
      .filter((media) => parseVariantMediaIds(media.alt).includes(variantId))
      .forEach(addMedia);
    mediaItems
      .filter((media) => {
        const mediaId = String(media.id);
        const hasVariantToken = parseVariantMediaIds(media.alt).length > 0;
        const isRoleMedia = ['lifestyle', 'dimensions', 'installation', 'shared'].includes(media.role);
        return !hasVariantToken && !featuredMediaIds.has(mediaId) && isRoleMedia;
      })
      .forEach(addMedia);
    if (!ordered.length) mediaItems.filter((media) => !featuredMediaIds.has(String(media.id))).forEach(addMedia);
    return orderVariantMedia(ordered, variant.featuredMediaId);
  };

  window.BeyimenProductUtils = { chooseCompatibleVariant, optionValueAvailable, orderVariantMedia, selectVariantMedia };

  const getStoredTheme = () => {
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  };

  const getSystemTheme = () => window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';

  const setTheme = (theme) => {
    document.documentElement.dataset.theme = theme;
    document.querySelectorAll('[data-theme-toggle]').forEach((button) => {
      const nextTheme = theme === 'dark' ? 'light' : 'dark';
      button.setAttribute('aria-label', `Use ${nextTheme} theme`);
      button.setAttribute('title', `Use ${nextTheme} theme`);
      button.setAttribute('aria-pressed', theme === 'light' ? 'true' : 'false');
    });
  };

  const setupThemeControls = (root = document) => {
    root.querySelectorAll('[data-theme-toggle]').forEach((button) => {
      if (button.dataset.themeReady === 'true') return;
      button.dataset.themeReady = 'true';
      button.addEventListener('click', () => {
        const nextTheme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
        setTheme(nextTheme);
        try {
          window.localStorage.setItem('beyimen-theme', nextTheme);
        } catch (error) {
          // The visual switch still works when storage is unavailable.
        }
      });
    });
    setTheme(document.documentElement.dataset.theme || getStoredTheme() || getSystemTheme());
  };

  const setupSystemTheme = () => {
    const preference = window.matchMedia('(prefers-color-scheme: light)');
    const handleChange = () => {
      if (!getStoredTheme()) setTheme(preference.matches ? 'light' : 'dark');
    };
    if (preference.addEventListener) preference.addEventListener('change', handleChange);
    else preference.addListener(handleChange);
  };

  const setupCarousel = (carousel, slideSelector, dotSelector, options = {}) => {
    if (!carousel || carousel.dataset.carouselReady === 'true') return;
    const slides = [...carousel.querySelectorAll(slideSelector)];
    const dots = [...carousel.querySelectorAll(dotSelector)];
    if (slides.length < 2) return;

    const { pauseOnHover = false, waitForInteraction = false } = options;
    const deferredBackgroundAttribute = 'data-background-image';
    carousel.dataset.carouselReady = 'true';
    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('active')));
    let timer;
    let unlocked = !waitForInteraction;
    const interval = Number(carousel.dataset.interval) || 6000;

    const loadSlide = (slide) => {
      const backgroundImage = slide?.getAttribute(deferredBackgroundAttribute);
      if (!backgroundImage) return;
      slide.style.backgroundImage = `url("${backgroundImage}")`;
      slide.removeAttribute(deferredBackgroundAttribute);
    };
    const show = (index) => {
      current = (index + slides.length) % slides.length;
      loadSlide(slides[current]);
      slides.forEach((slide, slideIndex) => slide.classList.toggle('active', slideIndex === current));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('active', dotIndex === current));
      if (unlocked) loadSlide(slides[(current + 1) % slides.length]);
    };
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (unlocked && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = window.setInterval(() => show(current + 1), interval);
      }
    };

    const interactionEvents = ['pointerdown', 'touchstart', 'wheel', 'scroll'];
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, unlock));
      window.removeEventListener('keydown', unlock);
      loadSlide(slides[(current + 1) % slides.length]);
      start();
    };

    carousel.querySelector('[data-prev]')?.addEventListener('click', () => { unlock(); show(current - 1); start(); });
    carousel.querySelector('[data-next]')?.addEventListener('click', () => { unlock(); show(current + 1); start(); });
    dots.forEach((dot) => dot.addEventListener('click', () => { unlock(); show(Number(dot.dataset.slide)); start(); }));
    if (pauseOnHover) {
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', start);
    }
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
    show(current);
    if (waitForInteraction) {
      interactionEvents.forEach((eventName) => window.addEventListener(eventName, unlock, { passive: true }));
      window.addEventListener('keydown', unlock);
    } else {
      start();
    }
  };

  const parseVariantMediaIds = (alt = '') => {
    const ids = [];
    const pattern = /variant:(\d+)/gi;
    let match = pattern.exec(alt);
    while (match) {
      ids.push(match[1]);
      match = pattern.exec(alt);
    }
    return ids;
  };

  const initializeProduct = (root) => {
    if (root.dataset.productReady === 'true') return;
    const payloadElement = root.querySelector('[data-product-variants]');
    if (!payloadElement) return;

    let payload;
    try {
      payload = JSON.parse(payloadElement.textContent);
    } catch (error) {
      return;
    }

    const gallery = root.querySelector('[data-product-gallery]');
    const thumbnailRail = gallery?.querySelector('[data-gallery-thumbnails]');
    const mainStage = gallery?.querySelector('[data-gallery-main]');
    const variantSelect = root.querySelector('[data-variant-select]');
    const price = root.querySelector('[data-product-price]');
    const availability = root.querySelector('[data-product-availability]');
    const addToCart = root.querySelector('[data-add-to-cart]');
    const optionButtons = [...root.querySelectorAll('[data-option-value]')];
    const mediaById = new Map(payload.media.map((media) => [String(media.id), {
      ...media,
      variantIds: parseVariantMediaIds(media.alt)
    }]));
    let selectedVariant = payload.variants.find((variant) => String(variant.id) === String(payload.selectedVariantId)) || payload.variants[0];
    let selectedOptions = selectedVariant ? [...selectedVariant.options] : [];

    const activateMedia = (mediaId) => {
      const media = mediaById.get(String(mediaId));
      const mainImage = mainStage?.querySelector('img');
      if (!media || !mainStage) return;

      const updateSelection = () => {
        thumbnailRail?.querySelectorAll('[data-media-id]').forEach((button) => {
          const active = String(button.dataset.mediaId) === String(media.id);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      };

      if (!mainImage) {
        const image = document.createElement('img');
        image.src = media.src;
        image.alt = media.alt || '';
        image.dataset.mediaId = media.id;
        mainStage.replaceChildren(image);
        updateSelection();
        return;
      }

      if (String(mainImage.dataset.mediaId) === String(media.id)) {
        updateSelection();
        return;
      }

      const preload = new Image();
      preload.onload = () => {
        mainImage.src = media.src;
        mainImage.alt = media.alt || '';
        mainImage.dataset.mediaId = media.id;
        updateSelection();
      };
      preload.onerror = () => {
        thumbnailRail?.querySelector(`[data-media-id="${media.id}"]`)?.setAttribute('hidden', '');
      };
      preload.src = media.src;
    };

    const mediaForVariant = (variant) => {
      return selectVariantMedia(payload.media, payload.variants, variant);
    };

    const renderGallery = (variant) => {
      if (!thumbnailRail) return;
      const mediaItems = mediaForVariant(variant);
      thumbnailRail.replaceChildren(...mediaItems.map((media, index) => {
        const button = document.createElement('button');
        const image = document.createElement('img');
        button.type = 'button';
        button.className = `product-gallery__thumbnail${index === 0 ? ' is-active' : ''}`;
        button.dataset.mediaId = media.id;
        button.setAttribute('aria-label', `View ${media.alt || 'product image'}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
        image.src = media.thumbnail;
        image.alt = media.alt || '';
        image.loading = 'lazy';
        image.addEventListener('error', () => button.setAttribute('hidden', ''));
        button.append(image);
        return button;
      }));
      if (mediaItems[0]) activateMedia(mediaItems[0].id);
    };

    const updateOptionControls = () => {
      optionButtons.forEach((button) => {
        const position = Number(button.dataset.optionPosition);
        const value = button.dataset.optionValue;
        const selected = selectedOptions[position] === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.disabled = !optionValueAvailable(payload.variants, position, value);
      });
    };

    const updateVariant = (variant, updateUrl = true) => {
      if (!variant) return;
      selectedVariant = variant;
      selectedOptions = [...variant.options];
      if (variantSelect) variantSelect.value = String(variant.id);
      if (price) price.textContent = variant.price;
      if (availability) availability.textContent = variant.available ? 'In stock' : 'Sold out';
      if (addToCart) {
        addToCart.disabled = !variant.available;
        addToCart.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      updateOptionControls();
      renderGallery(variant);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    optionButtons.forEach((button) => button.addEventListener('click', () => {
      const position = Number(button.dataset.optionPosition);
      updateVariant(chooseCompatibleVariant(payload.variants, selectedOptions, position, button.dataset.optionValue));
    }));
    variantSelect?.addEventListener('change', () => {
      updateVariant(payload.variants.find((variant) => String(variant.id) === variantSelect.value));
    });
    thumbnailRail?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-media-id]');
      if (button) activateMedia(button.dataset.mediaId);
    });

    root.classList.add('is-enhanced');
    root.dataset.productReady = 'true';
    updateVariant(selectedVariant, false);
  };

  const initializeTheme = (root = document) => {
    setupThemeControls(root);
    root.querySelectorAll('[data-hero-carousel]').forEach((carousel) => setupCarousel(carousel, '.hero-slide', '.hero-carousel-dot', { pauseOnHover: false, waitForInteraction: true }));
    root.querySelectorAll('[data-lookbook-carousel]').forEach((carousel) => setupCarousel(carousel, '.lookbook-carousel-slide', '.lookbook-carousel-dot', { pauseOnHover: true, waitForInteraction: false }));
    root.querySelectorAll('[data-product-root]').forEach(initializeProduct);
  };

  document.addEventListener('DOMContentLoaded', () => {
    setupSystemTheme();
    initializeTheme();
  });
  document.addEventListener('shopify:section:load', (event) => initializeTheme(event.target));
})();
